import React, { useState, useEffect } from 'react';
import { isSuperAdmin } from '../utils/userUtils';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { isAdminAuthorized } from '../utils/userUtils';
import { useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import DefaultModal from './components/modals/DefaultModal';

function CatmatUmImport() {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [success, setSuccess] = useState('');
  const [importStats, setImportStats] = useState(null);
  const [user, setUser] = useState(null);
  const [usuarioBind, setUsuarioBind] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [importHistory, setImportHistory] = useState([]);
  const [catmatUmList, setCatmatUmList] = useState([]);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    // Validar usuário e permissões
    const userData = localStorage.getItem('user');
    const usuarioBindData = localStorage.getItem('usuarioBind');
    if (!userData || !usuarioBindData) {
      navigate('/');
      return;
    }
    const parsedUser = JSON.parse(userData);
    const parsedUsuarioBind = JSON.parse(usuarioBindData);
    setUser(parsedUser);
    setUsuarioBind(parsedUsuarioBind);
    // manter apenas validação mínima do usuário presente; botões fazem validação
    loadStats();
    fetchImportHistory();
    fetchCatmatUm();
  }, [token, navigate]);

  const loadStats = async () => {
    try {
      const statsResponse = await axios.get(
        `${API_BASE_URL.CATMAT_UM}/estatisticas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportStats(statsResponse.data);
    } catch (err) {}
  };

  const fetchImportHistory = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL.CATMAT_UM}/import-log?tipoImportacao=CATMAT_UM`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportHistory(response.data || []);
    } catch (err) {
      setImportHistory([]);
    }
  };

  const fetchCatmatUm = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL.CATMAT_UM}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCatmatUmList(response.data);
    } catch (err) {
      setErrorMessage('Erro ao carregar dados de Catmat UF');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFullImport = async () => {
    if (!isSuperAdmin(user)) {
      setErrorMessage('Acesso negado. Você não possui permissão para executar esta ação.');
      setShowErrorModal(true);
      return;
    }
    setIsLoading(true);
    setSuccess('');
    setProgress('Iniciando importação completa...');
    try {
      const response = await axios.post(
        `${API_BASE_URL.CATMAT_UM}/importar-todos`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      if (response.data.success) {
        setSuccess(
          `Importação completa realizada com sucesso! ${response.data.totalImportados} registros importados.`
        );
        loadStats();
        fetchImportHistory();
        fetchCatmatUm();
      } else {
        setErrorMessage(
          response.data.message || 'Erro desconhecido na importação'
        );
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage('Erro ao executar importação completa');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
      setProgress('');
    }
  };

  const refreshStats = () => {
    loadStats();
    fetchImportHistory();
    fetchCatmatUm();
  };

  const formatDateTime = dateTime => {
    if (!dateTime) return 'N/A';
    try {
      const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
      if (isNaN(date.getTime())) return 'Data inválida';
      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'Erro na data';
    }
  };

  const formatNumber = number => {
    if (typeof number !== 'number') return number;
    return number.toLocaleString('pt-BR');
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Importação de Unidades de Medidas CATMAT" icon="bi-download" closeRoute="/home" backButton={false} />
        <PageContent>
        {isSuperAdmin(user) && (
          <Toolbar
            actions={[
              { key: 'import', label: isLoading ? 'Importando...' : 'Importar Dados', icon: isLoading ? 'fas fa-spinner fa-spin' : 'fas fa-download', variant: 'primary', disabled: isLoading, onClick: handleFullImport },
              { key: 'refresh', label: 'Atualizar Estatísticas', icon: 'fas fa-refresh', variant: 'outline-info', disabled: isLoading, onClick: refreshStats },
            ]}
          />
        )}
      
      {/* Histórico de Importações */}
      <TableContainer className="table-responsive mb-2">
        <h5 className="text-info fw-bold mb-2">
          <i className="fas fa-history me-2"></i>
          Histórico de Importações
        </h5>
        <table className="table table-bordered table-sm table-custom-radius">
          <TableHeader columns={[
            { label: 'Data/Hora' },
            { label: 'Tipo' },
            { label: 'Salvos (inserts/updates)' },
            { label: 'Usuário' },
            { label: 'Detalhes' },
          ]} />
          <tbody>
            {importHistory.length > 0 ? (
              importHistory.map((log, idx) => (
                <tr key={idx}>
                  <td>{formatDateTime(log.dataHora)}</td>
                  <td>{log.tipoImportacao}</td>
                  <td>{formatNumber(log.totalImportado)}</td>
                  <td>{log.usuario}</td>
                  <td>{log.detalhes}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted">
                  Nenhuma importação realizada ainda
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableContainer>
      <DefaultModal
        show={showErrorModal}
        type="error"
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default CatmatUmImport;
